import { useState, useEffect } from 'react';
import { countryData } from '../data/countryData';
import { logger } from '../utils/logger';
import { performanceMonitor } from '../utils/performance';

interface UseEmergencyNumberResult {
  emergencyNumber: string;
  country: string | null;
  loading: boolean;
  error: Error | null;
}

const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 300000 // 5 minutes
};

const DEFAULT_EMERGENCY = {
  number: '911',
  country: 'United States'
};

export function useEmergencyNumber(): UseEmergencyNumberResult {
  const [result, setResult] = useState<UseEmergencyNumberResult>({
    emergencyNumber: DEFAULT_EMERGENCY.number,
    country: DEFAULT_EMERGENCY.country,
    loading: true,
    error: null
  });

  useEffect(() => {
    const detectCountry = async () => {
      try {
        return await performanceMonitor.measure('detectCountry', async () => {
          // Check geolocation support
          if (!('geolocation' in navigator)) {
            throw new Error('Geolocation is not supported by your browser');
          }

          // Get position with timeout and options
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            const geolocationError = (error: GeolocationPositionError) => {
              switch (error.code) {
                case error.PERMISSION_DENIED:
                  reject(new Error('Location access denied. Using default emergency number.'));
                  break;
                case error.POSITION_UNAVAILABLE:
                  reject(new Error('Location information unavailable. Using default emergency number.'));
                  break;
                case error.TIMEOUT:
                  reject(new Error('Location request timed out. Using default emergency number.'));
                  break;
                default:
                  reject(new Error('Unknown geolocation error. Using default emergency number.'));
              }
            };

            navigator.geolocation.getCurrentPosition(
              resolve,
              geolocationError,
              GEOLOCATION_OPTIONS
            );
          });

          // Reverse geocoding with retry logic
          const geocodeWithRetry = async (retries = 3): Promise<Response> => {
            for (let i = 0; i < retries; i++) {
              try {
                const response = await fetch(
                  `https://nominatim.openstreetmap.org/reverse?lat=${position.coords.latitude}&lon=${position.coords.longitude}&format=json`,
                  {
                    headers: {
                      'Accept-Language': 'en-US,en;q=0.9',
                      'User-Agent': 'Gprov Healthcare Directory'
                    }
                  }
                );

                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`);
                }

                return response;
              } catch (error) {
                if (i === retries - 1) throw error;
                await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
              }
            }
            throw new Error('Failed to geocode after retries');
          };

          const response = await geocodeWithRetry();
          const data = await response.json();

          if (!data.address?.country) {
            throw new Error('Country information not available');
          }

          // Find matching country in our data
          const countryInfo = countryData.find(
            c => c.country.toLowerCase() === data.address.country.toLowerCase()
          );

          if (countryInfo) {
            logger.info('Country detected successfully', {
              country: countryInfo.country,
              emergencyNumber: countryInfo.emergencyNumber
            });

            setResult({
              emergencyNumber: countryInfo.emergencyNumber,
              country: countryInfo.country,
              loading: false,
              error: null
            });
            return;
          }

          // Fallback to default if country not found
          logger.warn('Country not found in database, using default', {
            detectedCountry: data.address.country
          });

          setResult({
            emergencyNumber: DEFAULT_EMERGENCY.number,
            country: DEFAULT_EMERGENCY.country,
            loading: false,
            error: null
          });
        });
      } catch (error) {
        logger.error('Error detecting country', error);
        
        setResult({
          emergencyNumber: DEFAULT_EMERGENCY.number,
          country: DEFAULT_EMERGENCY.country,
          loading: false,
          error: error instanceof Error ? error : new Error('Failed to detect country')
        });
      }
    };

    detectCountry();
  }, []);

  return result;
}
